// src/components/ErrorBoundary.tsx
// Class-based error boundary — catches unhandled render exceptions in any
// child component tree and shows a graceful fallback instead of a blank page.

import React, { Component, ReactNode } from "react";
import { reportError } from "../lib/monitoring";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, info.componentStack);
    reportError(error, "ErrorBoundary");
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
          <h2
            className="text-2xl font-semibold mb-4"
            style={{ color: "var(--color-rust)" }}
          >
            Something went wrong
          </h2>
          <p className="text-gray-600 mb-6 max-w-md">
            An unexpected error occurred. Please refresh the page and try again.
            If the problem persists, contact{" "}
            <a
              href="mailto:support@americanaandcompany.com"
              className="underline"
              style={{ color: "var(--color-rust)" }}
            >
              support@americanaandcompany.com
            </a>
            .
          </p>
          <button
            className="px-6 py-2 rounded font-semibold text-white"
            style={{ backgroundColor: "var(--color-rust)" }}
            onClick={() => this.setState({ hasError: false })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
